import {observer} from 'mobx-react-lite';
import {OuterWrapper} from './view-user-checkins.style';
import PageLayout from '@ui/templates/page-template/page-layout';
import {useLocation, useNavigate, useParams} from 'react-router';
import {Body1, Body2} from '@ui/atoms/typography';
import {useCallback, useMemo, useState} from 'react';
import {Button} from '@ui/atoms/button';
import {useStoreContext} from '@store/store-context';
import {ItemLoader} from '@ui/organisms/item-loader';
import {useViewUserCheckinsHook} from './view-user-checkins.hooks';
import {parseName} from '@pages/dashboard/you/you-components/you-checkins/you-checkins';
import {capitalize} from '@utils';
import {AlertBadge} from '@ui/atoms/icons/alert-badge';
import {updateTooltipStatus} from '@utils/firebase-request';
import {QuestionIconWrapper} from '@ui/molecules/tooltip/tooltip';
import {PlainButton} from '@ui/atoms/plain-button/plain-button';
import {checkinName} from '@utils/checkin-name';
import {
  SubmissionCard,
  SubmissionRecord,
} from '../../my_checkins/components/submission-card';
import {Avatar} from '@ui/atoms/avatar';
import {
  getCheckinCycleId,
  useCheckinsWithCycles,
} from '../../my_checkins/hooks/useCheckinsWithCycles';
import {TimeRangeDropdown} from '../../my_checkins/components/time-range-dropdown';
import {CheckinSubmissionDrawer} from '../../my_checkins/components/checkin-submission-drawer';

export const TooltipBadge = ({message}: {message: string}) => {
  return (
    <QuestionIconWrapper tooltip={true}>
      <AlertBadge />
      <span
        className="tooltip"
        style={{
          width: '300px',
          padding: '16px',
        }}>
        {message}
      </span>
    </QuestionIconWrapper>
  );
};
export const SecondaryTooltipBadge = ({tooltipId}: {tooltipId: string}) => {
  return (
    <PlainButton
      type="button"
      style={{marginLeft: '2px'}}
      onClick={() => updateTooltipStatus(tooltipId, {isDismissed: false})}>
      <AlertBadge />
    </PlainButton>
  );
};

interface IViewUserCheckins {}

// eslint-disable-next-line no-empty-pattern
export const ViewUserCheckins = observer(({}: IViewUserCheckins) => {
  const {checkinsId, type} = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const navigationState = location.state as
    | {user?: string; returnTo?: string}
    | undefined;
  const searchParams = new URLSearchParams(location.search);
  const userId = navigationState?.user || searchParams.get('user') || undefined;
  const returnTo =
    navigationState?.returnTo || searchParams.get('returnTo') || undefined;

  // State for time range selection
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(
    'All time',
  );

  // State for drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [
    selectedSubmission,
    setSelectedSubmission,
  ] = useState<SubmissionRecord | null>(null);

  const {
    checkinsStore: {checkinsDates},
    usersStore: {users},
  } = useStoreContext();

  const {handleSubmit, submitForm, loadingPage} = useViewUserCheckinsHook(
    checkinsId,
  );

  // Use the hook to get enriched checkins data with the user ID from state
  const {
    checkinsController,
    enrichedSubmissions,
    checkinsResponseRefetch,
  } = useCheckinsWithCycles({
    userId: userId,
    isTeamFeed: false,
  });

  // Handler for marking submission as reviewed
  const handleMarkAsReviewed = useCallback(
    async (submissionId: string) => {
      try {
        const response = await checkinsController.submitSpecificCheckin(
          {},
          submissionId,
          'report',
        );

        if (response) {
          // Refetch the checkins data instead of reloading the page
          await checkinsResponseRefetch();
        }
      } catch (error) {
        console.error('Error marking submission as reviewed:', error);
      }
    },
    [checkinsController, checkinsResponseRefetch],
  );

  // Filter submissions for the selected time range and prioritize the clicked submission
  const filteredSubmissions = useMemo(() => {
    if (!enrichedSubmissions || enrichedSubmissions.length === 0) return [];

    // Sort submissions so the one that was clicked (checkinsId) appears first
    const sorted = [...enrichedSubmissions].sort((a, b) => {
      if (a.id === checkinsId) return -1;
      if (b.id === checkinsId) return 1;
      return 0;
    });

    // For now, return all submissions regardless of time range
    // TODO: Implement time range filtering logic
    return sorted;
  }, [enrichedSubmissions, checkinsId]);

  const latestSendTimeByCycle = useMemo(() => {
    const latest = new Map<string, number>();
    enrichedSubmissions.forEach((submission) => {
      const cycleId = getCheckinCycleId(submission);
      const sentAt = Date.parse(submission.createdAt || '');
      if (cycleId && Number.isFinite(sentAt)) {
        latest.set(cycleId, Math.max(latest.get(cycleId) || 0, sentAt));
      }
    });
    return latest;
  }, [enrichedSubmissions]);
  const isLatestCycleSend = useCallback(
    (submission: SubmissionRecord) => {
      const cycleId = getCheckinCycleId(submission);
      const latestSentAt = cycleId
        ? latestSendTimeByCycle.get(cycleId)
        : undefined;
      const sentAt = Date.parse(submission.createdAt || '');
      return !latestSentAt || !Number.isFinite(sentAt)
        ? true
        : sentAt >= latestSentAt;
    },
    [latestSendTimeByCycle],
  );

  // Handler for viewing comments
  const handleViewComments = useCallback(
    (submissionId: string) => {
      const submission = filteredSubmissions.find((s) => s.id === submissionId);
      if (submission) {
        setSelectedSubmission(submission);
        setDrawerOpen(true);
      }
    },
    [filteredSubmissions],
  );

  // Get the current user data - fallback to submission user details if users array is empty
  const currentUser = useMemo(() => {
    // First try to get from users array
    const userFromStore = users?.find((u) => u.id === userId);
    if (userFromStore) return userFromStore;

    // Fallback to user details from the first submission if users array is empty
    if (filteredSubmissions?.[0]?.user) {
      return filteredSubmissions[0].user;
    }

    return null;
  }, [users, userId, filteredSubmissions]);

  // ETA Card component matching the screenshot
  const ETACard = useMemo(() => {
    const userName = parseName(currentUser);

    return (
      <div className="flex flex-col gap-4 rounded-[10px] py-4 px-6 border border-[#EDEDF2] bg-white">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Avatar
                tooltip={true}
                userId={currentUser?.id || ''}
                name={userName}
                size="lg"
                timezone={currentUser?.timezone}
                src={currentUser?.avatar?.url}
              />
              <div>
                <Body1 weight="semibold" kind="textDark">
                  {userName}
                </Body1>
                <Body2 kind="textBody">
                  {currentUser?.jobTitle || 'Team member'}
                </Body2>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button kind="secondary" style={{padding: '6px 16px'}}>
              Download .csv
            </Button>
            <TimeRangeDropdown
              selectedRange={selectedTimeRange}
              onRangeSelect={setSelectedTimeRange}
              showPlusIcon={false}
            />
          </div>
        </div>
      </div>
    );
  }, [currentUser, selectedTimeRange, setSelectedTimeRange]);

  return (
    <OuterWrapper onSubmit={handleSubmit(submitForm)}>
      {type === 'direct' && (
        <PageLayout
          title={`Submissions for ${
            filteredSubmissions?.[0]?.cycleName || capitalize(checkinName())
          }`}
          onClick={() =>
            navigate(
              returnTo ||
                `/checkins/direct-reports?week=${checkinsDates.weekOfYear}&year=${checkinsDates.year}`,
            )
          }>
          <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
            {/* ETA Card */}
            {ETACard}

            {loadingPage && (
              <div className="flex justify-center py-8">
                <ItemLoader />
              </div>
            )}

            {!loadingPage &&
            filteredSubmissions &&
            filteredSubmissions.length > 0 ? (
              <div className="flex flex-col gap-4">
                {filteredSubmissions.map((submission) => (
                  <SubmissionCard
                    key={submission.id}
                    submission={submission}
                    isLatestOccurrence={isLatestCycleSend(submission)}
                    isExpanded={true}
                    showViewSubmission={false}
                    showReviewActions={true}
                    showViewComments={true}
                    onMarkAsReviewed={handleMarkAsReviewed}
                    onViewComments={handleViewComments}
                    responseText={submission.pulse?.feedback || undefined}
                  />
                ))}
              </div>
            ) : (
              !loadingPage && (
                <div className="flex justify-center py-8">
                  <Body2 kind="textBody">
                    No submissions found for this user.
                  </Body2>
                </div>
              )
            )}
          </div>
        </PageLayout>
      )}

      {drawerOpen && selectedSubmission && (
        <CheckinSubmissionDrawer
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedSubmission(null);
          }}
          submission={selectedSubmission}
        />
      )}
    </OuterWrapper>
  );
});

ViewUserCheckins.displayName = 'ViewUserCheckins';
